"use client";

import { useFormikContext } from "formik";
import { useEffect, useRef, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import useToast from "@/contexts/toast-context";
import { WorkflowEditDialogMessages } from "@/messages/workflow/workflow-edit-dialog.messages";
import workflowService from "@/services/workflow.service";
import pxToRem from "@/themes/functions/pxToRem";

import { AVATAR_ACCEPTED_TYPES, WorkflowFormValues } from "../../form-data";

type Props = {
  messages: WorkflowEditDialogMessages;
  workflowId: number;
};

const PREVIEW_SIZE = 200;

function AgentAvatarImage({ messages, workflowId }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { values, setFieldValue } = useFormikContext<WorkflowFormValues>();
  const { showToast } = useToast();
  const { avatar } = values;
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    let objectUrl = "";
    let cancelled = false;

    const loadPreview = async () => {
      if (!avatar) {
        if (!cancelled) setPreviewUrl("");
        return;
      }

      if (avatar instanceof File) {
        objectUrl = URL.createObjectURL(avatar);
        if (!cancelled) setPreviewUrl(objectUrl);
        return;
      }

      if (!workflowId) {
        if (!cancelled) setPreviewUrl("");
        return;
      }

      try {
        // Preview autenticado — a URL pública /avatar não é servida no IIS/QAS.
        const blob = await workflowService.downloadAvatar(workflowId);
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setPreviewUrl(objectUrl);
      } catch {
        if (!cancelled) setPreviewUrl("");
      }
    };

    void loadPreview();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [avatar, workflowId]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    if (!AVATAR_ACCEPTED_TYPES.includes(file.type)) {
      showToast({ text: messages.avatarInvalidFormatError, type: "error" });
      event.target.value = "";
      return;
    }

    setFieldValue("avatar", file);
  };

  const handleRemove = () => {
    setFieldValue("avatar", null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const triggerFile = () => fileInputRef.current?.click();

  return (
    <MDBox display="flex" flexDirection="column" alignItems="center" textAlign="center" gap={1.5}>
      <input
        type="file"
        hidden
        onChange={handleChange}
        ref={fileInputRef}
        accept={AVATAR_ACCEPTED_TYPES.join(",")}
      />

      <MDTypography variant="button" fontWeight="bold">
        {messages.avatarTitle}
      </MDTypography>

      <MDBox
        borderRadius="xl"
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
        onClick={triggerFile}
        sx={{
          width: pxToRem(PREVIEW_SIZE),
          height: pxToRem(PREVIEW_SIZE),
          cursor: "pointer",
          border: "2px dashed #c4c9d4",
          // Fundo xadrez para evidenciar a transparência do sticker.
          backgroundColor: "#f4f5f8",
          backgroundImage:
            "linear-gradient(45deg, #e6e8ee 25%, transparent 25%, transparent 75%, #e6e8ee 75%), linear-gradient(45deg, #e6e8ee 25%, transparent 25%, transparent 75%, #e6e8ee 75%)",
          backgroundSize: "16px 16px",
          backgroundPosition: "0 0, 8px 8px",
          transition: "border-color .2s, box-shadow .2s",
          "&:hover": { borderColor: "#344767", boxShadow: 3 },
        }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={messages.avatarTitle}
            style={{ display: "block", objectFit: "contain", width: "100%", height: "100%" }}
          />
        ) : (
          <MDTypography variant="caption" color="text" px={2}>
            512x512
          </MDTypography>
        )}
      </MDBox>

      <MDBox display="flex" gap={1}>
        <MDButton variant="gradient" color="dark" size="small" onClick={triggerFile}>
          {avatar ? messages.avatarUpdateButton : messages.avatarAddButton}
        </MDButton>
        {avatar && (
          <MDButton variant="outlined" color="error" size="small" onClick={handleRemove}>
            {messages.avatarRemoveButton}
          </MDButton>
        )}
      </MDBox>

      <MDTypography variant="caption" color="text" sx={{ maxWidth: pxToRem(260) }}>
        {messages.avatarHint}
      </MDTypography>
    </MDBox>
  );
}

export default AgentAvatarImage;
